import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faArrowLeft,
  faDownload,
  faExternalLinkAlt,
  faFilePdf,
  faRefresh,
  faSearch,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  exportSettlementPdfRegenerationBatchProcessedRows,
  getSettlementPdfRegenerationBatchGeneratedFiles,
  getSettlementPdfRegenerationBatchHistory,
  getSettlementPdfRegenerationBatchProcessedRows,
} from "../../services/settlementApi";

const FILE_TYPE_LABELS = {
  pis: "PIS",
  pis_no_ppd: "PIS No PPD",
  form_2307: "Form 2307",
};

const SettlementPdfRegenerationBatchProcessedPage = () => {
  const navigate = useNavigate();
  const { uploadId } = useParams();

  const [uploadInfo, setUploadInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowType, setRowType] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState([]);
  const [generatedLoading, setGeneratedLoading] = useState(false);
  const [generatedFilesOpen, setGeneratedFilesOpen] = useState(false);
  const [filters, setFilters] = useState({
    prn: "",
    result: "",
    details: "",
  });
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);

  useEffect(() => {
    const fetchUploadInfo = async () => {
      try {
        const response = await getSettlementPdfRegenerationBatchHistory({ page: 1, limit: 1000 });
        const upload = response.data.data?.find((item) => item.upload_id === uploadId);
        if (upload) {
          setUploadInfo(upload);
        } else {
          toast.error("Upload not found.");
          navigate("/batch-regenerate-pis-form2307-history");
        }
      } catch (error) {
        toast.error("Failed to fetch upload information.");
        console.error(error);
      }
    };

    if (uploadId) fetchUploadInfo();
  }, [uploadId, navigate]);

  const fetchRows = useCallback(async () => {
    if (!uploadId) return;
    setLoading(true);
    try {
      const response = await getSettlementPdfRegenerationBatchProcessedRows(uploadId, {
        page: 1,
        limit: 5000,
        type: rowType,
      });
      setRows(response.data.data || []);
      setPage(1);
    } catch (error) {
      toast.error("Failed to fetch processed rows.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [uploadId, rowType]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const fetchGeneratedFiles = useCallback(async () => {
    if (!uploadId) return;
    setGeneratedLoading(true);
    try {
      const response = await getSettlementPdfRegenerationBatchGeneratedFiles(uploadId);
      setGeneratedFiles(response.data.data || []);
    } catch (error) {
      setGeneratedFiles([]);
      console.error(error);
    } finally {
      setGeneratedLoading(false);
    }
  }, [uploadId]);

  const filteredRows = useMemo(() => {
    const match = (value, filter) =>
      !filter || String(value ?? "").toLowerCase().includes(filter.toLowerCase());

    return rows.filter((row) => {
      if (!match(row.payment_ref_no, filters.prn)) return false;
      if (!match(row.result_type, filters.result)) return false;

      const detailText = [
        row.error_message,
        row.processed_by,
        row.form_2307_skipped_reason,
        row.pis_file,
        row.pis_no_ppd_file,
        row.form_2307_file,
        row.email_notification_status,
        row.email_notification_error,
        row.email_notification_outbox_id,
      ].filter(Boolean).join(" ");

      return match(detailText, filters.details);
    });
  }, [rows, filters]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredRows.slice(start, start + perPage);
  }, [filteredRows, page, perPage]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));
  const hasActiveFilters = filters.prn || filters.result || filters.details;

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ prn: "", result: "", details: "" });
    setPage(1);
  };

  const handleExport = async (type) => {
    if (!uploadId) return;
    setExporting(true);
    try {
      const response = await exportSettlementPdfRegenerationBatchProcessedRows(uploadId, { type });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `batch_regenerate_pis_form2307_${uploadId}_${type}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${type} rows successfully.`);
    } catch (error) {
      toast.error("Export failed.");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const handleToggleGeneratedFiles = () => {
    const nextOpen = !generatedFilesOpen;
    setGeneratedFilesOpen(nextOpen);
    if (nextOpen && generatedFiles.length === 0 && !generatedLoading) {
      fetchGeneratedFiles();
    }
  };

  const getResultBadge = (resultType) => {
    if (resultType === "success") {
      return <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">Success</span>;
    }
    return <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">Failed</span>;
  };

  const getEmailNotificationBadge = (row) => {
    const requested = Number(row.resend_email_notifications || 0) === 1;
    if (!requested) {
      return <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-600">No</span>;
    }

    const status = String(row.email_notification_status || "").toLowerCase();
    if (!status) {
      return <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">Requested</span>;
    }

    const statusClass = {
      sent: "bg-green-100 text-green-800",
      queued: "bg-blue-100 text-blue-800",
      sending: "bg-blue-100 text-blue-800",
      pending: "bg-yellow-100 text-yellow-800",
      failed: "bg-red-100 text-red-800",
      skipped: "bg-gray-100 text-gray-700",
      cancelled: "bg-gray-100 text-gray-700",
    }[status] || "bg-gray-100 text-gray-700";

    return <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${statusClass}`}>{status}</span>;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const openLink = (url) => {
    if (!url) {
      toast.warning("No public link available.");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const fileLinkButton = (label, fileName, url) => (
    <button
      type="button"
      onClick={() => openLink(url)}
      disabled={!url}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400"
      title={fileName || label}
    >
      <FontAwesomeIcon icon={faExternalLinkAlt} />
      {label}
    </button>
  );

  if (!uploadInfo) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="text-center py-12">
          <p className="text-gray-600">Loading upload information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">
        <div className="mb-6 flex items-center gap-4 flex-wrap">
          <button
            onClick={() => navigate("/batch-regenerate-pis-form2307-history")}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg flex items-center gap-2 text-sm font-medium"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Batch Regenerate Details</h1>
            <p className="text-gray-600 text-sm mt-0.5">{uploadInfo.original_file_name}</p>
          </div>
          <button
            onClick={fetchRows}
            disabled={loading}
            className="ml-auto px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded-lg flex items-center gap-2 text-sm"
          >
            <FontAwesomeIcon icon={faRefresh} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-5 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Rows</p>
              <p className="text-2xl font-bold text-gray-800">{uploadInfo.total_rows || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Success</p>
              <p className="text-2xl font-bold text-green-600">{uploadInfo.success_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Failed</p>
              <p className="text-2xl font-bold text-red-600">{uploadInfo.failed_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <p className={`text-lg font-bold ${uploadInfo.status === "completed" ? "text-green-600" : uploadInfo.status === "failed" ? "text-red-600" : "text-yellow-600"}`}>
                {(uploadInfo.status || "").charAt(0).toUpperCase() + (uploadInfo.status || "").slice(1)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded By</p>
              <p className="text-sm font-semibold text-gray-800">{uploadInfo.uploaded_by_name || "-"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded At</p>
              <p className="text-sm font-semibold text-gray-800">{formatDate(uploadInfo.created_at)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md mb-5 overflow-hidden">
          <button
            onClick={handleToggleGeneratedFiles}
            className="w-full px-5 py-3 bg-gray-50 hover:bg-gray-100 border-b border-gray-200 flex items-center justify-between text-left"
          >
            <span className="font-semibold text-gray-800 flex items-center gap-2">
              <FontAwesomeIcon icon={faFilePdf} className="text-red-500" />
              Generated PDF Files
            </span>
            <span className="text-xs text-gray-500">{generatedFilesOpen ? "Hide" : "Show"}</span>
          </button>

          {generatedFilesOpen && (
            <div className="p-4">
              {generatedLoading ? (
                <p className="text-sm text-gray-500">Loading generated files...</p>
              ) : generatedFiles.length === 0 ? (
                <p className="text-sm text-gray-500">No generated files found.</p>
              ) : (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {generatedFiles.map((file) => (
                    <div key={file.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-gray-500">{FILE_TYPE_LABELS[file.file_type] || file.file_type}</p>
                          <p className="text-sm font-semibold text-gray-800 truncate" title={file.file_name}>{file.file_name}</p>
                          <p className="text-xs text-gray-400 font-mono truncate">{file.settlement_id}</p>
                        </div>
                        <button
                          onClick={() => openLink(file.public_share_url || file.signed_url)}
                          className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                        >
                          Open
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-5">
          <div className="flex flex-col lg:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Search PRN</label>
              <input
                type="text"
                value={filters.prn}
                onChange={(e) => handleFilterChange("prn", e.target.value)}
                placeholder="Payment reference number..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Result</label>
              <select
                value={filters.result}
                onChange={(e) => handleFilterChange("result", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Details</label>
              <input
                type="text"
                value={filters.details}
                onChange={(e) => handleFilterChange("details", e.target.value)}
                placeholder="File name, error, processed by..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="flex gap-2">
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg flex items-center gap-1.5 text-sm"
                  title="Clear filters"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
              <button
                onClick={() => setPage(1)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faSearch} />
                Apply
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {["all", "success", "failed"].map((item) => (
              <button
                key={item}
                onClick={() => setRowType(item)}
                className={`px-3 py-1.5 rounded text-xs font-semibold capitalize ${
                  rowType === item ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {item}
              </button>
            ))}
            <button
              onClick={() => handleExport("all")}
              disabled={exporting}
              className="ml-auto px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded text-xs font-semibold flex items-center gap-1.5"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export All
            </button>
            <button
              onClick={() => handleExport(rowType)}
              disabled={exporting}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded text-xs font-semibold flex items-center gap-1.5"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export Current
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 border-b border-gray-300">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Row</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Result</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">PRN</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Settlement ID</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Files</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Details</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Processed At</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                  </tr>
                ) : paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">No rows found</td>
                  </tr>
                ) : (
                  paginatedRows.map((row) => (
                    <tr key={`${row.result_type}-${row.id}`} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{row.row_number}</td>
                      <td className="px-4 py-3">{getResultBadge(row.result_type)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-800">{row.payment_ref_no || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{row.settlement_id || "-"}</td>
                      <td className="px-4 py-3">
                        {row.result_type === "success" ? (
                          <div className="flex flex-wrap gap-1.5">
                            {fileLinkButton("PIS", row.pis_file, row.public_share_urls?.pis)}
                            {fileLinkButton("No PPD", row.pis_no_ppd_file, row.public_share_urls?.pis_no_ppd)}
                            {Number(row.form_2307_generated || 0) === 1
                              ? fileLinkButton("2307", row.form_2307_file, row.public_share_urls?.form_2307)
                              : <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-500">{row.form_2307_skipped_reason || "No Form 2307"}</span>}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">{getEmailNotificationBadge(row)}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-md">
                        {row.error_message || row.email_notification_error || row.form_2307_skipped_reason || (row.processed_by ? `Processed by ${row.processed_by}` : "-")}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{formatDate(row.processed_at || row.failed_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {filteredRows.length === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
              {Math.min(page * perPage, filteredRows.length)} of {filteredRows.length} rows
            </div>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || loading}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-sm font-medium text-gray-700">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages || loading}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default SettlementPdfRegenerationBatchProcessedPage;
